import { useEffect, useMemo, useRef, useState } from 'react';
import { geoAlbersUsa, geoPath, type GeoPermissibleObjects } from 'd3-geo';
import { feature } from 'topojson-client';
import type { Topology, GeometryCollection } from 'topojson-specification';
import type { FeatureCollection, Geometry } from 'geojson';
import type { AtlasRecord, MapDefinition, StateSummary } from '@grs-atlas/shared';
import { US_STATE_NAMES, type UsStateCode } from '@grs-atlas/shared';
import statesTopology from 'us-atlas/states-10m.json';

type StateFeatureProps = { name: string };

const FIPS_TO_CODE: Record<string, UsStateCode> = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA', '08': 'CO',
  '09': 'CT', '10': 'DE', '11': 'DC', '12': 'FL', '13': 'GA', '15': 'HI',
  '16': 'ID', '17': 'IL', '18': 'IN', '19': 'IA', '20': 'KS', '21': 'KY',
  '22': 'LA', '23': 'ME', '24': 'MD', '25': 'MA', '26': 'MI', '27': 'MN',
  '28': 'MS', '29': 'MO', '30': 'MT', '31': 'NE', '32': 'NV', '33': 'NH',
  '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND', '39': 'OH',
  '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI', '45': 'SC', '46': 'SD',
  '47': 'TN', '48': 'TX', '49': 'UT', '50': 'VT', '51': 'VA', '53': 'WA',
  '54': 'WV', '55': 'WI', '56': 'WY',
};

const TOOLTIP_RECORD_LIMIT = 5;

function fieldDisplayValue(
  record: AtlasRecord,
  key: string,
  map?: MapDefinition,
): string {
  if (key === 'name') return record.name;
  if (key === 'stateCodes') return record.stateCodes.join(', ');
  if (key === 'status') return record.status;
  if (key === 'tags') return record.tags.join(', ');
  if (key === 'updatedAt') return new Date(record.updatedAt).toLocaleString();
  const v = record.fields[key];
  if (typeof v === 'boolean') {
    if (!v) return '';
    return map?.fields.find((f) => f.key === key)?.label ?? key;
  }
  if (Array.isArray(v)) return v.join(', ');
  return v == null ? '' : String(v);
}

function formatRecordTooltipLine(
  record: AtlasRecord,
  keys: string[],
  map?: MapDefinition,
): string {
  return keys
    .map((key) => fieldDisplayValue(record, key, map).trim())
    .filter(Boolean)
    .join(' · ');
}

interface UsMapProps {
  states: StateSummary[];
  map?: MapDefinition;
  records?: AtlasRecord[];
  selectedState?: string;
  onSelectState: (code: string | undefined) => void;
}

export function UsMap({
  states,
  map,
  records = [],
  selectedState,
  onSelectState,
}: UsMapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    title: string;
    lines: string[];
  } | null>(null);

  const byCode = useMemo(() => {
    const m = new Map<string, StateSummary>();
    for (const s of states) m.set(s.stateCode, s);
    return m;
  }, [states]);

  const recordsByState = useMemo(() => {
    const m = new Map<string, AtlasRecord[]>();
    for (const record of records) {
      for (const code of record.stateCodes) {
        const list = m.get(code);
        if (list) list.push(record);
        else m.set(code, [record]);
      }
    }
    return m;
  }, [records]);

  const tooltipKeys = useMemo(() => {
    if (map?.tooltipFields.length) return map.tooltipFields;
    return ['name'] as string[];
  }, [map]);

  const geoJson = useMemo(() => {
    const topo = statesTopology as unknown as Topology<{ states: GeometryCollection }>;
    return feature(topo, topo.objects.states) as FeatureCollection<Geometry, StateFeatureProps>;
  }, []);

  const { path, width, height } = useMemo(() => {
    const width = 960;
    const height = 600;
    const projection = geoAlbersUsa().fitSize([width, height], geoJson);
    const path = geoPath(projection);
    return { path, width, height };
  }, [geoJson]);

  const legendItems = useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of states) {
      const label = String(s.metrics.label ?? '');
      const color = String(s.metrics.color ?? '#e8eef5');
      if (label && !seen.has(label)) seen.set(label, color);
    }
    return [...seen.entries()];
  }, [states]);

  useEffect(() => {
    // Focus selected state for a11y announcements
  }, [selectedState]);

  const buildTooltip = (code: UsStateCode, name: string, label: string) => {
    const stateRecords = recordsByState.get(code) ?? [];
    const lines = stateRecords
      .slice(0, TOOLTIP_RECORD_LIMIT)
      .map((record) => formatRecordTooltipLine(record, tooltipKeys, map))
      .filter(Boolean);
    const remaining = stateRecords.length - TOOLTIP_RECORD_LIMIT;
    if (remaining > 0) lines.push(`+${remaining} more`);
    return {
      title: `${name} (${code}): ${label}`,
      lines,
    };
  };

  return (
    <div style={{ position: 'relative', maxWidth: '100%', overflow: 'hidden' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="United States map. Use arrow keys or tab to select a state."
        style={{ width: '100%', height: 'auto', maxHeight: '70vh', display: 'block' }}
      >
        {geoJson.features.map((f) => {
          const id = String(f.id ?? '').padStart(2, '0');
          const code = FIPS_TO_CODE[id];
          if (!code) return null;
          const summary = byCode.get(code);
          const fill = String(summary?.metrics.color ?? '#e8eef5');
          const name = US_STATE_NAMES[code] ?? f.properties.name;
          const count = summary?.recordCount ?? 0;
          const label = String(summary?.metrics.label ?? `${count} records`);
          const isSelected = selectedState === code;

          return (
            <path
              key={code}
              className={`state-path${isSelected ? ' selected' : ''}`}
              d={path(f as GeoPermissibleObjects) ?? undefined}
              fill={fill}
              tabIndex={0}
              role="button"
              aria-label={`${name}: ${label}, ${count} records`}
              aria-pressed={isSelected}
              onClick={() => onSelectState(isSelected ? undefined : code)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectState(isSelected ? undefined : code);
                }
              }}
              onMouseEnter={(e) => {
                const rect = svgRef.current?.getBoundingClientRect();
                if (!rect) return;
                const content = buildTooltip(code, name, label);
                setTooltip({
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top,
                  ...content,
                });
              }}
              onMouseMove={(e) => {
                const rect = svgRef.current?.getBoundingClientRect();
                if (!rect) return;
                setTooltip((prev) =>
                  prev
                    ? {
                        ...prev,
                        x: e.clientX - rect.left,
                        y: e.clientY - rect.top,
                      }
                    : prev,
                );
              }}
              onMouseLeave={() => setTooltip(null)}
            />
          );
        })}
      </svg>

      {tooltip && (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            top: Math.max(0, tooltip.y - 12 - Math.min(180, 28 + tooltip.lines.length * 16)),
            left: Math.max(8, Math.min(tooltip.x, (svgRef.current?.clientWidth ?? 200) - 220)),
            maxWidth: 'min(18rem, calc(100% - 1rem))',
            background: '#1a2332',
            color: 'white',
            padding: '0.4rem 0.65rem',
            borderRadius: 6,
            fontSize: 12,
            lineHeight: 1.35,
            pointerEvents: 'none',
            zIndex: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          }}
        >
          <div style={{ fontWeight: 600 }}>{tooltip.title}</div>
          {tooltip.lines.map((line, i) => (
            <div key={i} style={{ opacity: 0.92, marginTop: i === 0 ? 4 : 2 }}>
              {line}
            </div>
          ))}
        </div>
      )}

      <div className="legend" aria-label="Map legend">
        {legendItems.map(([label, color]) => (
          <div className="legend-item" key={label}>
            <span className="swatch" style={{ background: color }} aria-hidden />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
